import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { MASTER_BUS_ID } from '@busswerks/shared'
import {
  hasNativeAudioPlugin,
  nativeGetCapabilities,
  nativeListInputs,
  nativeListOutputs,
  subscribeNativeDeviceChanges,
  type NativeAudioCapabilities,
} from '@/lib/nativeAudio'

/**
 * Enumerated audio I/O devices + the user's selection. Kept separate
 * from `midiStore` because the two systems evolve independently —
 * `audioDevicesStore` handles speakers/mics (WebRTC API) while MIDI
 * handles controllers (Web MIDI API).
 *
 * Device labels are empty strings until the page has been granted
 * microphone or camera permission at least once. That's a browser
 * privacy protection; output device labels are NOT gated by mic
 * permission in Chromium but ARE in Firefox. Callers should show
 * the id (`default`, `communications`, or a generic "Output #n") as
 * a fallback when the label is empty.
 *
 * Output switching is feature-detected at runtime — not every browser
 * supports routing Web Audio to a non-default sink. `canSwitchOutput`
 * reflects whether `AudioContext({ sinkId })` OR `setSinkId` on an
 * `<audio>` element is usable. When false, the UI should disable the
 * output picker and surface "System default (native)" instead.
 */

export interface AudioDeviceInfo {
  /** Raw deviceId from `MediaDeviceInfo.deviceId`. Opaque — pass
   *  back to getUserMedia / setSinkId verbatim. */
  id: string
  /** User-visible label. May be empty when the app lacks mic/camera
   *  permission. Consumers should fall back to the id + index. */
  label: string
  /**
   * How many channels the device exposes. `0` = unknown (the web
   * path can't cheaply determine this before `getUserMedia` runs;
   * native plugin always populates it). UI treats 0 as "assume
   * stereo" and hides per-channel pickers for that device.
   */
  channelCount: number
  /** Per-channel labels (e.g. "Mic 1", "Line In 3"). Empty when
   *  the device doesn't report them; UI falls back to "Ch N". */
  channelNames: string[]
}

/** Sentinel indicating "use the OS-level default" — shown as a
 *  first option in the picker so the user can always revert. */
export const DEFAULT_OUTPUT_ID = 'default'
export const DEFAULT_INPUT_ID = 'default'

interface AudioDevicesState {
  supported: boolean
  canSwitchOutput: boolean
  /**
   * Scope of an output-device selection — lets the UI word helper
   * text honestly. `'system'` on Catalyst (changes macOS default
   * for every app). `'per-session'` on web (setSinkId, per-tab).
   * `'none'` when switching isn't supported at all.
   */
  outputScope: 'system' | 'app' | 'per-session' | 'none'
  outputs: AudioDeviceInfo[]
  inputs: AudioDeviceInfo[]
  selectedOutputId: string
  selectedInputId: string
  /**
   * Mixer bus id the live audio input routes to. Absent bus → falls
   * back to master at schedule time. Persisted so reconnecting an
   * interface puts the mic back on the same channel the user last
   * configured. Default `MASTER_BUS_ID`.
   */
  inputBusId: string
  /**
   * When true, the input stream is audible through the chosen bus
   * (pass-through monitoring). When false, the source node exists
   * but is disconnected — useful for silent-capture workflows in
   * Phase 3 (record audio without routing it to speakers, avoiding
   * feedback on a laptop mic).
   *
   * Gates the initial `getUserMedia` prompt too — turning this on
   * is when the browser asks for permission.
   */
  inputMonitorEnabled: boolean

  setSupported: (v: boolean) => void
  setCanSwitchOutput: (v: boolean) => void
  setOutputScope: (scope: AudioDevicesState['outputScope']) => void
  setOutputs: (outputs: AudioDeviceInfo[]) => void
  setInputs: (inputs: AudioDeviceInfo[]) => void
  setSelectedOutputId: (id: string) => void
  setSelectedInputId: (id: string) => void
  setInputBusId: (id: string) => void
  setInputMonitorEnabled: (v: boolean) => void
  /**
   * Patch the known channel count for a single output. The web
   * `enumerateDevices` API doesn't expose channel info, so we
   * observe it via `AudioContext.destination.maxChannelCount` after
   * the sink changes and back-fill the matching output entry here.
   * No-op if the device id isn't in the current list.
   */
  setOutputChannelCount: (deviceId: string, channelCount: number) => void
  /**
   * Same pattern for inputs — populated via `probeInputChannelCount`
   * which opens a brief `getUserMedia` stream to read the real count
   * (enumerateDevices hides it on web). No-op when the id isn't in
   * the list.
   */
  setInputChannelCount: (deviceId: string, channelCount: number) => void
}

export const useAudioDevicesStore = create<AudioDevicesState>()(
  persist(
    set => ({
      supported:
        typeof navigator !== 'undefined' &&
        Boolean(navigator.mediaDevices?.enumerateDevices),
      canSwitchOutput: false,
      outputScope: 'none' as const,
      outputs: [],
      inputs: [],
      selectedOutputId: DEFAULT_OUTPUT_ID,
      selectedInputId: DEFAULT_INPUT_ID,
      inputBusId: MASTER_BUS_ID,
      inputMonitorEnabled: false,

      setSupported: v => set({ supported: v }),
      setCanSwitchOutput: v => set({ canSwitchOutput: v }),
      setOutputScope: scope => set({ outputScope: scope }),
      setOutputs: outputs => set({ outputs }),
      setInputs: inputs => set({ inputs }),
      setSelectedOutputId: id => set({ selectedOutputId: id }),
      setSelectedInputId: id => set({ selectedInputId: id }),
      setInputBusId: id => set({ inputBusId: id }),
      setInputMonitorEnabled: v => set({ inputMonitorEnabled: v }),
      setOutputChannelCount: (deviceId, channelCount) =>
        set(state => {
          const normalized = Math.max(0, Math.floor(channelCount))
          const next = state.outputs.map(o =>
            o.id === deviceId && o.channelCount !== normalized
              ? { ...o, channelCount: normalized }
              : o,
          )
          // Reference-identical no-op when nothing changed, to keep
          // the Zustand subscribers that drive `syncBuses` etc. from
          // re-running on every `setDeviceId` round-trip.
          return next.every((o, i) => o === state.outputs[i])
            ? {}
            : { outputs: next }
        }),
      setInputChannelCount: (deviceId, channelCount) =>
        set(state => {
          const normalized = Math.max(0, Math.floor(channelCount))
          const next = state.inputs.map(i =>
            i.id === deviceId && i.channelCount !== normalized
              ? { ...i, channelCount: normalized }
              : i,
          )
          return next.every((i, idx) => i === state.inputs[idx])
            ? {}
            : { inputs: next }
        }),
    }),
    {
      name: 'busswerks-audio-devices',
      // Only user intent persists (selection ids). The device lists
      // themselves are runtime — re-enumerated on every page load so
      // stale entries don't point at disconnected hardware.
      partialize: state => ({
        selectedOutputId: state.selectedOutputId,
        selectedInputId: state.selectedInputId,
        inputBusId: state.inputBusId,
        // Deliberately NOT persisted: monitor-enabled. Returning to
        // the app shouldn't silently re-request mic permission on
        // load — the user has to explicitly re-enable each session.
      }),
      storage: createJSONStorage(() =>
        typeof window === 'undefined' ? memoryStorage() : window.localStorage,
      ),
    },
  ),
)

/**
 * Populate the store from the browser's device list. Safe to call
 * more than once and on `devicechange` events; the setters are
 * idempotent and the UI just re-renders with the latest snapshot.
 * Call sites (AudioPanel mount, OutputSink setup) should invoke
 * this at startup AND register for `devicechange` to stay current.
 *
 * Re-evaluates `supported` on every call. This matters on native
 * runtimes: WKWebView hides `navigator.mediaDevices.enumerateDevices`
 * until the app's Info.plist / entitlements expose mic intent. A
 * fresh Xcode build that newly includes those descriptors goes from
 * `supported: false → true` without requiring a restart of the
 * Zustand store's init-time sniff.
 */
export async function refreshAudioDevices(): Promise<void> {
  const state = useAudioDevicesStore.getState()
  if (typeof navigator === 'undefined') return
  // Native path — BusswerksAudio plugin handles iOS / iPadOS /
  // Mac Catalyst where `navigator.mediaDevices` is hidden on the
  // custom-scheme origin. Plugin always works on those platforms
  // once installed; mark supported eagerly.
  if (hasNativeAudioPlugin()) {
    if (!state.supported) state.setSupported(true)
    try {
      const [outputs, inputs, caps] = await Promise.all([
        nativeListOutputs(),
        nativeListInputs(),
        nativeGetCapabilities().catch<NativeAudioCapabilities>(() => ({
          canSwitchOutput: false,
          outputScope: 'none',
          canSwitchInput: false,
        })),
      ])
      state.setOutputs(
        outputs.map(d => ({
          id: d.id,
          label: d.name,
          channelCount: d.channelCount,
          channelNames: d.channelNames,
        })),
      )
      state.setInputs(
        inputs.map(d => ({
          id: d.id,
          label: d.name,
          channelCount: d.channelCount,
          channelNames: d.channelNames,
        })),
      )
      if (state.canSwitchOutput !== caps.canSwitchOutput) {
        state.setCanSwitchOutput(caps.canSwitchOutput)
      }
      if (state.outputScope !== caps.outputScope) {
        state.setOutputScope(caps.outputScope)
      }
    } catch {
      // Plugin not yet registered (fresh install before npx cap
      // sync) or session setup failed. Leave the lists as-is.
    }
    return
  }

  // Web path — browsers that expose `mediaDevices.enumerateDevices`.
  const hasApi = Boolean(navigator.mediaDevices?.enumerateDevices)
  if (hasApi !== state.supported) state.setSupported(hasApi)
  if (!hasApi) return
  try {
    const all = await navigator.mediaDevices.enumerateDevices()
    const outputs: AudioDeviceInfo[] = []
    const inputs: AudioDeviceInfo[] = []
    for (const d of all) {
      // Web's enumerateDevices doesn't expose channel info before a
      // MediaStream opens, so we set `0` = unknown. Phase 5b's
      // engine falls back to stereo when channelCount is 0, matching
      // the web's historical default.
      const base: AudioDeviceInfo = {
        id: d.deviceId,
        label: d.label,
        channelCount: 0,
        channelNames: [],
      }
      if (d.kind === 'audiooutput') {
        outputs.push(base)
      } else if (d.kind === 'audioinput') {
        inputs.push(base)
      }
    }
    state.setOutputs(outputs)
    state.setInputs(inputs)
  } catch {
    // Quietly degrade — enumerateDevices rarely throws on modern
    // browsers, but Safari has historically rejected when called
    // before any user gesture. The picker just shows an empty list.
  }
}

/**
 * One-time native-side route-change subscription — forwards hot-
 * plug events from AVAudioSession to a fresh `refreshAudioDevices`.
 * No-op on web (the web path already subscribes to
 * `navigator.mediaDevices.devicechange` inside `ensureOutputSink`).
 *
 * Called once at app startup via `useAudioEngine.ensureOutputSink`.
 */
let nativeRouteSubscribed = false
export function ensureNativeDeviceChangeSubscription(): void {
  if (nativeRouteSubscribed) return
  if (!hasNativeAudioPlugin()) return
  nativeRouteSubscribed = true
  subscribeNativeDeviceChanges(() => {
    void refreshAudioDevices()
  })
}

/**
 * Open a short-lived stream on the given input device to read its
 * actual channel count, then write the result onto the matching
 * entry in `inputs`. Web-only — on native the plugin already carries
 * real counts in its device listing, so this no-ops.
 *
 * Opens a stream because web's `enumerateDevices` doesn't expose
 * channel info. Browsers prompt for mic permission on first call
 * per origin; subsequent probes reuse the cached grant. We stop
 * tracks immediately after reading settings so no mic indicator
 * lingers in the UI.
 *
 * Errors (permission denied, device gone) are swallowed — the
 * caller doesn't need a hard failure for a "best effort" probe; the
 * UI just falls back to assuming 2 channels.
 */
export async function probeInputChannelCount(deviceId: string): Promise<void> {
  if (typeof navigator === 'undefined') return
  if (!navigator.mediaDevices?.getUserMedia) return
  if (hasNativeAudioPlugin()) return
  const useDefault = !deviceId || deviceId === DEFAULT_INPUT_ID
  const audio: MediaTrackConstraints = useDefault
    ? {}
    : { deviceId: { exact: deviceId } }
  audio.echoCancellation = false
  audio.noiseSuppression = false
  audio.autoGainControl = false
  // Ask for up to 32 — browsers silently cap to device capacity and
  // report the real count in `getSettings().channelCount`.
  audio.channelCount = 32
  let stream: MediaStream | null = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio })
    const track = stream.getAudioTracks()[0]
    const settings = track?.getSettings()
    const count = Math.max(1, settings?.channelCount ?? 2)
    // Use the device's actual id when the user picked "default" so
    // the UI sees the count on both the real device entry + the
    // sentinel row.
    const resolvedId =
      useDefault && track?.getSettings().deviceId
        ? String(track.getSettings().deviceId)
        : deviceId
    useAudioDevicesStore.getState().setInputChannelCount(resolvedId, count)
    if (useDefault) {
      useAudioDevicesStore
        .getState()
        .setInputChannelCount(DEFAULT_INPUT_ID, count)
    }
  } catch {
    /* swallow — probe is best-effort, callers fall back to 2-ch */
  } finally {
    if (stream) {
      for (const t of stream.getTracks()) t.stop()
    }
  }
}

function memoryStorage(): Storage {
  const store = new Map<string, string>()
  return {
    get length() {
      return store.size
    },
    clear: () => store.clear(),
    getItem: key => store.get(key) ?? null,
    key: index => Array.from(store.keys())[index] ?? null,
    removeItem: key => {
      store.delete(key)
    },
    setItem: (key, value) => {
      store.set(key, value)
    },
  }
}
